import { useEffect, useRef, useState } from 'react';
import Card from '../common/Card';
import UpgradePlanAction from './UpgradePlanAction';
import UpgradePlanModal from './UpgradePlanModal';
import { useReactivateSubscription, useUpgradeOptions } from '../../hooks/useBilling';
import { upgradeDeepLinkNotice, useUpgradeDeepLink } from '../../hooks/useUpgradeDeepLink';
import type { BillingStatus, Subscription, UpgradeOption } from '../../types';

interface Props {
  billing: BillingStatus;
  onMigrate: () => void;
  subscriptions?: Subscription[];
}

export default function LegacyBillingCard({ billing, onMigrate, subscriptions }: Props) {
  const reactivate = useReactivateSubscription();
  const { data: upgradeOptions } = useUpgradeOptions();
  const [upgradeOption, setUpgradeOption] = useState<UpgradeOption | null>(null);
  const upgradeDeepLinkStatus = useUpgradeDeepLink(upgradeOptions, setUpgradeOption);
  const deepLinkNotice = upgradeDeepLinkNotice(upgradeDeepLinkStatus);
  const reactivated = new URLSearchParams(window.location.search).get('reactivated') === 'true';
  const suspendedSubs = subscriptions?.filter(
    (s) => s.status === 'suspended' || s.status === 'archived'
  ) ?? [];
  const hasDedicatedIpEligibleSub = subscriptions?.some((s) => s.dedicated_ip_eligible) ?? false;
  const providerName = billing.provider === 'foxy' ? 'FoxyCart' : 'Billsby';
  const billsbyButtonRef = useRef<HTMLDivElement>(null);

  const upgradeOptionBySubId = new Map(
    upgradeOptions?.subscriptions.map(o => [o.subscription_id, o]) ?? []
  );

  // Get Billsby customer and subscription IDs for self-service portal
  const billsbyAccount = billing.billing_accounts?.find(a => a.provider === 'billsby');
  const billsbySubscription = billing.subscriptions?.find(s => s.provider === 'billsby');
  const billsbyCustomerId = billsbyAccount?.customer_id;
  const billsbySubscriptionId = billsbySubscription?.external_subscription_id;

  // Create Billsby button using DOM to bypass React's javascript: URL blocking
  // Then call scanDomBillsby() to have Billsby attach click handlers
  useEffect(() => {
    if (billing.provider === 'billsby' && billsbyCustomerId && billsbyButtonRef.current) {
      const container = billsbyButtonRef.current;
      container.innerHTML = ''; // Clear existing content

      const anchor = document.createElement('a');
      anchor.href = 'javascript:void(0)';
      anchor.setAttribute('data-billsby-type', 'account');
      anchor.setAttribute('data-billsby-customer', billsbyCustomerId);
      if (billsbySubscriptionId) {
        anchor.setAttribute('data-billsby-subscription', billsbySubscriptionId);
      }
      anchor.className = 'w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium transition-colors cursor-pointer';
      anchor.innerHTML = `
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
        Manage Billsby Billing
      `;
      container.appendChild(anchor);

      // Tell Billsby to rescan the DOM and attach click handlers to our new element
      const win = window as unknown as { scanDomBillsby?: () => void };
      if (typeof win.scanDomBillsby === 'function') {
        win.scanDomBillsby();
      }
    }
  }, [billing.provider, billsbyCustomerId, billsbySubscriptionId]);

  const formatCurrency = (amount: number, currency: string) => {
    // Backend already returns amounts in dollars, not cents
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  return (
    <Card className="p-6">
      {reactivated && (
        <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
          <p className="text-sm text-green-800">
            Your service is being restored. You'll receive an email when it's ready.
          </p>
        </div>
      )}
      {deepLinkNotice && (
        <p className={`text-sm mb-4 ${deepLinkNotice.tone === 'error' ? 'text-red-700' : 'text-amber-700'}`}>
          {deepLinkNotice.message}
        </p>
      )}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Billing Migration Recommended
          </h2>
          <p className="text-gray-600">
            Your subscription is currently managed through {providerName}. We're
            transitioning to a new billing system using Stripe for improved
            features and support.
          </p>
        </div>

        <div className="flex-shrink-0">
          <svg
            className="w-12 h-12 text-amber-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
      </div>

      {billing.has_balance_due &&
        billing.balance_amount &&
        billing.balance_currency && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="font-medium text-red-800">
                Outstanding Balance:{' '}
                {formatCurrency(
                  billing.balance_amount,
                  billing.balance_currency
                )}
              </span>
            </div>
            <p className="text-sm text-red-700 mt-1">
              Please clear your balance before migrating to our new billing
              system.
            </p>
          </div>
        )}

      {billing.subscriptions && billing.subscriptions.length > 0 && (
        <div className="border-t border-gray-200 pt-4 mb-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Current Subscriptions
          </h3>
          <div className="space-y-2">
            {billing.subscriptions.map((sub) => (
              <div
                key={sub.subscription_id}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-gray-900">
                  {sub.plan_name}
                  {sub.hostname && (
                    <span className="text-gray-500 ml-2">({sub.hostname})</span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      sub.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {sub.status}
                  </span>
                  {sub.status === 'active' && (
                    <UpgradePlanAction
                      option={upgradeOptionBySubId.get(sub.subscription_id)}
                      onUpgradeClick={setUpgradeOption}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suspended/archived subscriptions from subscriptions API */}
      {suspendedSubs.length > 0 && (
        <div className="space-y-3 mb-4">
          {suspendedSubs.map((sub) => (
            <div
              key={sub.id}
              className="border border-amber-200 bg-amber-50 rounded-md p-3"
            >
              <div className="flex items-center gap-2 text-sm mb-1">
                <span className="font-medium text-gray-900">
                  {sub.plan || 'Unknown'}
                </span>
                <span className="text-gray-500">{sub.hostname}</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {sub.status === 'archived' ? 'Archived' : 'Suspended'}
                </span>
              </div>
              <p className="text-xs text-amber-800 mb-2">
                Your service has been suspended due to non-payment.
                A one-time restoration fee applies.
              </p>
              <button
                onClick={() => reactivate.mutate(sub.id)}
                disabled={reactivate.isPending}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 font-medium transition-colors"
              >
                {reactivate.isPending ? 'Redirecting...' : 'Reactivate Service'}
              </button>
            </div>
          ))}

          {reactivate.isError && (
            <p className="text-sm text-red-600">
              {reactivate.error instanceof Error ? reactivate.error.message : 'Failed to start reactivation'}
            </p>
          )}
        </div>
      )}

      {/* Hide migration/Billsby buttons when subscription is suspended — reactivation handles billing migration automatically */}
      {suspendedSubs.length === 0 && (
        <>
          {/* Info box about adding subscriptions */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Want to add another subscription?
                </p>
                <p className="text-sm text-blue-800 mt-1">
                  Migrate to Stripe billing first, then you can easily add new
                  subscriptions from the portal.
                </p>
                {hasDedicatedIpEligibleSub && (
                  <p className="text-sm text-blue-800 mt-1">
                    Dedicated IP addresses can also be added from the portal once
                    you migrate.
                  </p>
                )}
              </div>
            </div>
          </div>

          {billing.can_migrate_to_stripe ? (
            <button
              onClick={onMigrate}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors"
            >
              Migrate to New Billing
            </button>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
              <p className="text-sm text-gray-600">
                {billing.message ||
                  'Please contact support to migrate your subscription.'}
              </p>
              <a
                href="mailto:support@cloudunifi.com"
                className="mt-2 inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Contact Support
                <svg
                  className="w-4 h-4 ml-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </a>
            </div>
          )}

          {/* Billsby Self-Service Portal Button - rendered via DOM to bypass React's javascript: URL blocking */}
          {billing.provider === 'billsby' && billsbyCustomerId && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div ref={billsbyButtonRef}></div>
              <p className="mt-2 text-xs text-gray-500 text-center">
                Update payment method, view invoices, or cancel subscription
              </p>
            </div>
          )}
        </>
      )}

      {upgradeOption && (
        <UpgradePlanModal
          isOpen
          onClose={() => setUpgradeOption(null)}
          option={upgradeOption}
        />
      )}
    </Card>
  );
}
